<script setup lang="ts">
import { computed, onMounted } from 'vue';

import ButtonText from '@/shared/components/UI/Buttons/ButtonText.vue';
import ErrorScreen from '@/shared/components/UI/ErrorScreen.vue';
import IconSpinner from '@/shared/components/UI/Icons/IconSpinner.vue';
import { useTranslations } from '@/shared/composables/useTranslations';
import { useYarnPatterns } from '@/shared/composables/yarnPatterns/useYarnPatterns';
import { useProductStore } from '@/shared/pinia/modules/product';
import { trackHobbiiEvent } from '@/shared/utils/tracking';

import YarnPatternCard from './YarnPatternCard.vue';

const { t } = useTranslations();
const productStore = useProductStore();

// Get productGroupId from store (similar to how pattern products gets patternId)
const productGroupId = computed(() => productStore.legacyId || '');

const {
  patterns,
  loading,
  error,
  retry,
  retrying,
  hasMore,
  loadMore,
  loadingMore
} = useYarnPatterns(productGroupId.value);

onMounted(() => {
  trackHobbiiEvent('feature_view', {
    category: 'section',
    item_name: 'yarn_patterns',
    label: 'Yarn Patterns'
  });
});
</script>

<template>
  <div class="flex w-full flex-col gap-6">
    <!-- Loading State -->
    <div
      v-if="loading && !retrying && !loadingMore"
      class="flex min-h-80 flex-col items-center justify-center py-8 text-center"
    >
      <IconSpinner class="mb-4 text-3xl text-primary" />
      <p class="text-gray-500">{{ t('patterns.products.pattern.loading') }}</p>
    </div>

    <!-- Error State -->
    <ErrorScreen
      v-else-if="error && !retrying"
      :error="t('patterns.products.pattern.error-description')"
      :retry="retry"
    />

    <!-- Content -->
    <template v-else>
      <!-- Empty State -->
      <div v-if="patterns.length === 0" class="py-8 text-center">
        <p class="text-gray-500">
          {{ t('patterns.products.pattern.no-patterns-found') }}
        </p>
      </div>

      <!-- Patterns Grid (like pattern products grid) -->
      <div v-else class="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <YarnPatternCard
          v-for="pattern in patterns"
          :key="pattern.sku"
          :pattern="pattern"
        />
      </div>

      <!-- Load More -->
      <div v-if="hasMore" class="mt-6 text-center">
        <ButtonText :loading="loadingMore" class="px-6 py-2" @click="loadMore">
          {{ t('patterns.products.pattern.load-more') }}
        </ButtonText>
      </div>

      <!-- Retrying indicator -->
      <div v-if="retrying" class="py-4 text-center">
        <IconSpinner class="text-lg text-primary" />
      </div>
    </template>
  </div>
</template>
